import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  ManageAccounts as ManageAccountsIcon,
  MonetizationOn as MonetizationIcon,
  PeopleAlt as PeopleAltIcon,
  Settings as SettingsIcon,
  Work as WorkIcon,
  Workspaces as WorkspacesIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { ItemDetailsLayout } from 'src/components/common';
import { DemoDashboardEntityToggle } from 'src/components/demoDashboardMode';
import {
  usePeoPayrollProviderById,
  usePeoPayrollProviderParamId,
} from 'src/hooks/peoPayrollProvider';
import { MenuItem } from 'src/types/menu';
import { SxProp } from 'src/types/mui';
import { buildUrl } from 'src/utils/router';

export const PeoPayrollProvidersDetailLayout: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const peoPayrollProviderId = usePeoPayrollProviderParamId();
  const { peoPayrollProvider, updatePeoPayrollProviderLogo, isPartnership, loading } =
    usePeoPayrollProviderById(peoPayrollProviderId);

  const baseRoute = React.useMemo(() => {
    return isPartnership
      ? appRoutes.partnerships.detail.peoPayrollProvider
      : appRoutes.peoPayrollProviders.detail;
  }, [isPartnership]);

  const builtRoutes = React.useMemo(
    () => ({
      companyInformation: buildUrl(baseRoute.companyInformation, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),

      staff: buildUrl(baseRoute.staff, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),
      peos: buildUrl(baseRoute.peos, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),
      employers: buildUrl(baseRoute.employers, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),
      employees: buildUrl(baseRoute.employees, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),
      billing: buildUrl(baseRoute.billing, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),
      settings: buildUrl(baseRoute.settings, {
        [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
      }),
    }),
    [baseRoute, peoPayrollProviderId],
  );

  const menuItems: Array<MenuItem> = React.useMemo(() => {
    return [
      {
        link: builtRoutes.companyInformation,
        icon: AccountBalanceIcon,
        title: 'Company Information',
      },

      {
        link: builtRoutes.staff,
        icon: ManageAccountsIcon,
        title: 'Staff',
      },
      {
        link: builtRoutes.peos,
        icon: WorkspacesIcon,
        title: 'PEOs',
      },
      {
        link: builtRoutes.employers,
        icon: WorkIcon,
        title: 'Employers',
      },
      {
        link: builtRoutes.employees,
        icon: PeopleAltIcon,
        title: 'Employees',
        hide: isPartnership,
      },
      {
        link: builtRoutes.billing,
        icon: MonetizationIcon,
        title: 'Billing',
      },
      {
        link: builtRoutes.settings,
        icon: SettingsIcon,
        title: 'Settings',
      },
    ];
  }, [builtRoutes, isPartnership]);

  return (
    <ItemDetailsLayout
      name={peoPayrollProvider?.displayName}
      subtitle={isPartnership ? 'Partnership' : 'PEO Payroll Provider'}
      logoUrl={peoPayrollProvider?.logo?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updatePeoPayrollProviderLogo,
        loading: loading,
      }}
      loading={loading}
      sidebarFooter={
        <DemoDashboardEntityToggle
          entityType="peoPayrollProviders"
          entityId={peoPayrollProviderId}
        />
      }
    >
      <Box sx={childrenContainerStyles}>{children}</Box>
    </ItemDetailsLayout>
  );
};

const childrenContainerStyles: SxProp = theme => ({
  [theme.breakpoints.up('sm')]: {
    minHeight: '80vh',
  },
});
